import { useQuery, type QueryKey, type QueryObserverOptions } from '@tanstack/react-query';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import { type ReceivableInvoiceAPI } from 'receivable-invoices-js/react/api/models/receivable-invoice';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';

interface Params {
  id: string;
}

interface QueryResponse {
  data: ReceivableInvoiceAPI;
}

interface CustomOptions {
  onError?: (error: unknown) => void;
}

const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/invoices/shared`;

export function fetchReceivableInvoiceKey(queryParams: Params) {
  return ['receivable-invoice', queryParams] as QueryKey;
}

export async function fetchReceivableInvoice(params: Params, customOptions?: CustomOptions) {
  const { onError } = customOptions || {};

  try {
    const responseData = await qontoClient<QueryResponse>(`${URL}/${params.id}`);

    const { attributes, id, relationships } = responseData.data;
    const normalizedResponse = {
      ...attributes,
      id,
      // Extract only the IDs from relationships - we don't need the full relationship objects
      receivableInvoicingUploadId:
        relationships?.receivable_invoicing_upload?.data &&
        !Array.isArray(relationships.receivable_invoicing_upload.data)
          ? relationships.receivable_invoicing_upload.data.id
          : undefined,
      termsConditionsUploadId:
        relationships?.terms_conditions_upload?.data &&
        !Array.isArray(relationships.terms_conditions_upload.data)
          ? relationships.terms_conditions_upload.data.id
          : undefined,
    };

    return camelizeKeys(normalizedResponse);
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

export type FetchReceivableInvoiceResponse = Awaited<ReturnType<typeof fetchReceivableInvoice>>;

export function useFetchReceivableInvoice(
  params: Params,
  customOptions?: CustomOptions,
  queryOptions?: Partial<QueryObserverOptions<FetchReceivableInvoiceResponse>>
) {
  return useQuery({
    queryKey: fetchReceivableInvoiceKey(params),
    queryFn: () => fetchReceivableInvoice(params, customOptions),
    ...queryOptions,
  });
}
